import React from 'react';
import { Badge, Flex, Text } from '@/components/ui';
import { FormFieldHoverCard } from './FormFieldHoverCard';
import type { FormField } from '@/types/studyForms';

interface FormFieldsListProps {
  fields: FormField[];
  variant?: 'badges' | 'table';
  maxHeight?: string;
  usePopover?: boolean; // Use Popover instead of HoverCard to avoid nested hover conflicts
}

export const FormFieldsList: React.FC<FormFieldsListProps> = ({
  fields,
  variant = 'badges',
  maxHeight = '300px',
  usePopover = false,
}) => {
  if (!fields || fields.length === 0) {
    return (
      <Text size="2" color="gray">
        No fields available
      </Text>
    );
  }

  if (variant === 'badges') {
    return (
      <Flex
        gap="2"
        wrap="wrap"
        style={{ maxHeight, overflow: 'auto' }}
      >
        {fields.map((field, index) => (
          <FormFieldHoverCard key={index} field={field} usePopover={usePopover}>
            <Badge
              color="gray"
              variant="outlined"
              size="xsmall"
              style={{
                padding: '4px 8px',
                cursor: 'pointer',
              }}
            >
              <Flex as="span" align="center" gap="1">
                <Text as="span" size="2xsmall" weight="bold">
                  {field.dataPoint}
                </Text>
                {field.required && (
                  <Text as="span" size="2xsmall" color="red">
                    *
                  </Text>
                )}
              </Flex>
            </Badge>
          </FormFieldHoverCard>
        ))}
      </Flex>
    );
  }

  // Table variant
  return (
    <div style={{ maxHeight, overflow: 'auto' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr auto auto',
          gap: '8px',
          fontSize: '12px',
        }}
      >
        {/* Header */}
        <Text
          size="2xsmall"
          weight="bold"
          style={{
            padding: '8px',
            borderBottom: '1px solid var(--gray-6)',
          }}
        >
          Field
        </Text>
        <Text
          size="2xsmall"
          weight="bold"
          style={{
            padding: '8px',
            borderBottom: '1px solid var(--gray-6)',
          }}
        >
          Label
        </Text>
        <Text
          size="2xsmall"
          weight="bold"
          style={{
            padding: '8px',
            borderBottom: '1px solid var(--gray-6)',
          }}
        >
          Type
        </Text>
        <Text
          size="2xsmall"
          weight="bold"
          style={{
            padding: '8px',
            borderBottom: '1px solid var(--gray-6)',
          }}
        >
          Required
        </Text>

        {/* Rows */}
        {fields.map((field, index) => (
          <React.Fragment key={index}>
            <FormFieldHoverCard field={field}>
              <div
                style={{
                  padding: '8px',
                  borderBottom: '1px solid var(--gray-4)',
                  cursor: 'pointer',
                }}
              >
                <Text size="2xsmall" weight="bold">
                  {field.dataPoint}
                </Text>
              </div>
            </FormFieldHoverCard>
            <FormFieldHoverCard field={field}>
              <div
                style={{
                  padding: '8px',
                  borderBottom: '1px solid var(--gray-4)',
                  cursor: 'pointer',
                }}
              >
                <Text size="2xsmall">{field.label}</Text>
              </div>
            </FormFieldHoverCard>
            <div
              style={{
                padding: '8px',
                borderBottom: '1px solid var(--gray-4)',
              }}
            >
              <Badge color="blue" variant="outlined" size="xsmall">
                {field.type}
              </Badge>
            </div>
            <div
              style={{
                padding: '8px',
                borderBottom: '1px solid var(--gray-4)',
              }}
            >
              {field.required ? (
                <Badge color="red" variant="soft" size="xsmall">
                  Required
                </Badge>
              ) : (
                <Text size="2xsmall" color="gray">
                  Optional
                </Text>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

